import { FormEvent, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";

export default function AccessCode() {
  const { me } = useAuth();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const afterReset = params.get("reset") === "1";

  if (!me) return null; // loading
  if (me.signed_in) return <Navigate to="/practice" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.checkAccessCode(code.trim());
      // The code is correct. Carry it through to the BlazerID step in router
      // state (not in the URL — never expose the access code in browser history).
      navigate("/enter-blazerid", { state: { code: code.trim() }, replace: false });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError("That code didn’t match. Double-check the one your professor shared.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="stack-lg">
        <header>
          <h1 className="h1">Pilot access</h1>
          <p className="lede">
            Enter the access code your professor shared with the class.
          </p>
          {afterReset && (
            <p
              className="muted"
              style={{
                marginTop: 8,
                padding: "8px 12px",
                background: "rgba(0,0,0,0.04)",
                borderRadius: 6,
                fontSize: 14,
              }}
            >
              Data reset. Sign in again.
            </p>
          )}
        </header>

        <form onSubmit={onSubmit} className="card stack">
          <label htmlFor="code" style={{ fontWeight: 500 }}>
            Access code
          </label>
          <input
            id="code"
            className="input"
            type="text"
            autoFocus
            autoComplete="one-time-code"
            placeholder="e.g. SUMMER2026"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.05em" }}
            disabled={submitting}
          />
          {error && <div className="muted" style={{ color: "var(--negative)" }}>{error}</div>}
          <button className="btn btn--lg" type="submit" disabled={submitting || !code.trim()}>
            {submitting ? "Checking…" : "Continue"}
          </button>
        </form>

        <p className="muted" style={{ fontSize: 14 }}>
          Next, we’ll ask for your <strong>BlazerID</strong> so your practice is
          tagged to you. The pilot is opt-in and lives on our own infrastructure;
          no UAB sign-in.
        </p>
      </div>
    </div>
  );
}
